import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TIME_PER_QUESTION_MS } from '../utils/homeHelpers';

const useTriviaSession = () => {
  const { i18n } = useTranslation('home');
  const [showTrivia, setShowTrivia] = useState(false);
  const [loadingTrivia, setLoadingTrivia] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [pickedIdx, setPickedIdx] = useState(-1);
  const [timeLeft, setTimeLeft] = useState(TIME_PER_QUESTION_MS);
  const [ticking, setTicking] = useState(false);
  const [questionStartedAt, setQuestionStartedAt] = useState(null);
  const [finished, setFinished] = useState(false);
  const [playedToday, setPlayedToday] = useState(false);
  const [triviaMode, setTriviaMode] = useState('idle');

  const triviaAdvanceTimeoutRef = useRef(null);
  const sessionStartedRef = useRef(false);
  const spoiledRef = useRef(false);
  const triviaOpenInflightRef = useRef(false);

  const current = questions[qIndex] || null;
  const total = questions.length || 0;

  const shareScore = useCallback(() => {
    const text = i18n.t('trivia.share.text', { ns: 'home', score, total });
    const shareData = {
      title: i18n.t('trivia.share.title', { ns: 'home' }),
      text,
      url: typeof window !== 'undefined' ? window.location.origin : '',
    };

    if (navigator.share) {
      navigator.share(shareData).catch(() => {
        try {
          navigator.clipboard.writeText(`${text} ${shareData.url}`);
          alert(i18n.t('trivia.share.copied', { ns: 'home' }));
        } catch {}
      });
      return;
    }

    try {
      navigator.clipboard.writeText(`${text} ${shareData.url}`);
      alert(i18n.t('trivia.share.copied', { ns: 'home' }));
    } catch {
      alert(text);
    }
  }, [i18n, score, total]);

  const resetTriviaBaseState = useCallback(() => {
    setQuestions([]);
    setQIndex(0);
    setScore(0);
    setAnswered(false);
    setPickedIdx(-1);
    setTicking(false);
    setTimeLeft(TIME_PER_QUESTION_MS);
    setQuestionStartedAt(null);
    setFinished(false);
  }, []);

  const startTriviaPlayState = useCallback((nextQuestions = null) => {
    if (Array.isArray(nextQuestions)) {
      setQuestions(nextQuestions);
    }
    setQIndex(0);
    setScore(0);
    setAnswered(false);
    setPickedIdx(-1);
    setTimeLeft(TIME_PER_QUESTION_MS);
    setQuestionStartedAt(Date.now());
    setTicking(true);
  }, []);

  const markTriviaAsPlayedMode = useCallback(() => {
    setPlayedToday(true);
    setTriviaMode('played');
    resetTriviaBaseState();
  }, [resetTriviaBaseState]);

  return {
    showTrivia,
    setShowTrivia,
    loadingTrivia,
    setLoadingTrivia,
    questions,
    setQuestions,
    qIndex,
    setQIndex,
    score,
    setScore,
    answered,
    setAnswered,
    pickedIdx,
    setPickedIdx,
    timeLeft,
    setTimeLeft,
    ticking,
    setTicking,
    questionStartedAt,
    setQuestionStartedAt,
    finished,
    setFinished,
    playedToday,
    setPlayedToday,
    triviaMode,
    setTriviaMode,
    triviaAdvanceTimeoutRef,
    sessionStartedRef,
    spoiledRef,
    triviaOpenInflightRef,
    current,
    total,
    shareScore,
    resetTriviaBaseState,
    startTriviaPlayState,
    markTriviaAsPlayedMode,
  };
};

export default useTriviaSession;
